import GoEspagne from './components/GoEspagne';
import GoSlug from './components/GoSlug';
import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import './App.css';
import { ADMIN_PATH, ADMIN_PATH_ALIAS } from './adminPath';
import Header from './components/Header';
import Footer from './components/Footer';
import AssistantChat from './components/AssistantChat';
import MobileNav, { mobileNavVisible } from './components/MobileNav';
import Home from './pages/Home';
import Trips from './pages/Trips';
import TripDetail from './pages/TripDetail';
import About from './pages/About';
import HowToRegister from './pages/HowToRegister';
import News from './pages/News';
import Contact from './pages/Contact';
import AdminDashboard from './pages/AdminDashboard';
import BioLinks from './pages/BioLinks';
import PracticalAdvice from './pages/PracticalAdvice';
import FAQ from './pages/FAQ';
import InstallerAppli from './pages/InstallerAppli';
import TutoInscription from './pages/TutoInscription';
import SpainSimulator from './pages/SpainSimulator'; // Iter 684 — Devis Espagne réactivé (données 2027 via API)
import RegistrationForm from './pages/RegistrationForm';
import PaymentPage from './pages/PaymentPage';
import InformationsAdministratives from './pages/InformationsAdministratives';
import CompletedTrips from './pages/CompletedTrips';
import AccessGatePreview from './pages/AccessGatePreview';
import AccessGate, { getStoredToken } from './pages/AccessGate';
import PrivacyPolicy from './pages/PrivacyPolicy';
import LegalNotices from './pages/LegalNotices';
import CGV from './pages/CGV';
import ProspectionPage from './pages/ProspectionPage';
import ChartePlacementBus from './pages/ChartePlacementBus';
import MesPieces from './pages/MesPieces';
import MonBus from './pages/MonBus';
import EspaceAdherent from './pages/EspaceAdherent';
import InscriptionGroupe from './pages/InscriptionGroupe';
import SondagePage from './pages/SondagePage';
import VerificationVol from './pages/VerificationVol';
import { InstallBanner } from './components/InstallBanner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// Iter 848 (Kamel) — provenance : d'où vient le visiteur (lien court / réseau / referrer), gardée 30 jours jusqu'à l'inscription
const Provenance = () => {
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      let source = (p.get('src') || p.get('utm_source') || p.get('s') || '').toLowerCase().replace(/[^a-z0-9_-]/g, '');
      const campagne = (p.get('c') || '').toLowerCase().replace(/[^a-z0-9_-]/g, '');
      const lien = (p.get('lc') || '').toLowerCase().replace(/[^a-z0-9_-]/g, '');
      const parrain = (p.get('p') || '').toLowerCase().replace(/[^a-z0-9_-]/g, '');  // Iter 908 — code du parrain (partage espace adhérent)
      if (!source) {
        const ref = (document.referrer || '').toLowerCase();
        if (ref.includes('tiktok')) source = 'tiktok';
        else if (ref.includes('instagram')) source = 'instagram';
        else if (ref.includes('facebook') || ref.includes('fb.')) source = 'facebook';
        else if (ref.includes('snapchat')) source = 'snapchat';
        else if (ref.includes('whatsapp') || ref.includes('wa.me')) source = 'whatsapp';
        else if (ref.includes('google')) source = 'google';
      }
      const prev = JSON.parse(localStorage.getItem('raav_provenance') || 'null');
      const frais = prev && Date.now() - (prev.at || 0) < 30 * 86400000;
      // une vraie source (lien/réseau) remplace toujours ; un referrer vide ne remplace jamais une source connue
      if (source && (!frais || !prev.source || prev.source === 'direct' || campagne || lien || parrain)) {
        localStorage.setItem('raav_provenance', JSON.stringify({ source, campagne: campagne || null, lien: lien || null, parrain: parrain || null, at: Date.now() }));
      } else if (!frais) {
        localStorage.setItem('raav_provenance', JSON.stringify({ source: 'direct', campagne: null, lien: null, at: Date.now() }));
      }
      // Iter 907 — ouverture d'un lien partagé (compteur Prospection)
      const m = (p.get('src') || '') === 'partage' && window.location.pathname.match(/^\/(?:voyage|trips)\/([^/]+)/);
      if (m && !sessionStorage.getItem(`raav_visite_partage_${m[1]}`)) {
        sessionStorage.setItem(`raav_visite_partage_${m[1]}`, '1');  // une ouverture = une visite, pas une par rendu
        fetch(`${API}/partages`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ trip_id: m[1], type: 'visite', origine: 'lien' }) }).catch(() => {});
      }
    } catch { /* best effort */ }
  }, []);
  return null;
};

// Composant wrapper pour conditionner Header/Footer selon la route
const Layout = ({ children }) => {
  const { pathname } = useLocation();
  const hideChrome = pathname === '/apercu-verrouillage';
  // Iter 491 — bulle assistant IA publique (pas en admin, ni dans l'Espace Adhérent qui a la sienne, ni sur les pages à token)
  const assistantPublic = !hideChrome && !/^\/(admin|espace-adherent|mes-pieces|mon-bus|sondage|verification-vol)/.test(pathname);
  // Iter 904 — barre mobile en bas : on réserve sa hauteur sous le pied de page (smartphone seulement)
  const barreMobile = !hideChrome && (mobileNavVisible(pathname) || /^\/(voyage|trips)\//.test(pathname));
  return (
    <div className={`App min-h-screen flex flex-col ${barreMobile ? 'barre-mobile' : ''}`}>
      {!hideChrome && <Header />}
      <main className="flex-grow">{children}</main>
      {assistantPublic && <AssistantChat mode="public" />}
      {!hideChrome && <Footer />}
      {!hideChrome && <MobileNav />}
    </div>
  );
};

// Garde d'accès global : si gate activé et pas de token valide → écran AccessGate
const AccessGuard = ({ children }) => {
  const { pathname } = useLocation();
  const [status, setStatus] = useState({ loading: true, gateEnabled: false, hasToken: false });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/access/status`);
        const gateEnabled = !!r.data?.gate_enabled;
        const token = getStoredToken();
        let hasToken = !!token;
        // Si on a un token, on le valide côté serveur (sel a pu changer = revoke-all)
        if (hasToken) {
          try {
            const v = await axios.post(`${API}/access/validate-token`, { token });
            hasToken = !!v.data?.valid;
          } catch { hasToken = false; }
        }
        if (mounted) setStatus({ loading: false, gateEnabled, hasToken });
      } catch {
        if (mounted) setStatus({ loading: false, gateEnabled: false, hasToken: false });
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (status.loading) return null; // pas de flash d'écran

  // Le panneau admin, l'aperçu et les liens personnels voyageurs restent toujours accessibles
  const alwaysOpen = [ADMIN_PATH, ADMIN_PATH_ALIAS, '/apercu-verrouillage'];
  if (alwaysOpen.some(p => p.toLowerCase() === pathname.toLowerCase()) || pathname.startsWith('/mes-pieces/') || pathname.startsWith('/mon-bus/') || pathname.startsWith('/verification-vol/') || pathname.startsWith('/espace-adherent')) return children;

  if (status.gateEnabled && !status.hasToken) {
    return <AccessGate onUnlock={() => setStatus(s => ({ ...s, hasToken: true }))} />;
  }
  return children;
};

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Provenance />
      <InstallBanner />
      <AccessGuard>
        <Layout>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/voyages" element={<Trips />} />
          <Route path="/bio" element={<BioLinks />} />
          <Route path="/liens" element={<BioLinks />} />
          <Route path="/voyage/:id" element={<TripDetail />} />
          <Route path="/trips/:id" element={<TripDetail />} />
          <Route path="/a-propos" element={<About />} />
          <Route path="/qui-sommes-nous" element={<About />} />
          <Route path="/inscription" element={<RegistrationForm />} />
          {/* Iter 576 — « Inscription express » supprimée : anciens liens → formulaire unique */}
          <Route path="/inscription-express" element={<Navigate to="/inscription" replace />} />
          <Route path="/inscription-groupe" element={<InscriptionGroupe />} />
          <Route path="/formulaire-inscription" element={<RegistrationForm />} />
          <Route path="/rattrapage" element={<Navigate to="/inscription" replace />} />
          <Route path="/informations-administratives" element={<InformationsAdministratives />} />
          <Route path="/comment-sinscrire" element={<HowToRegister />} />
          <Route path="/espagne" element={<GoEspagne />} />
          <Route path="/restez-informe" element={<ProspectionPage />} />
          <Route path="/restez-informes" element={<ProspectionPage />} />
          <Route path="/payer" element={<PaymentPage />} />
          <Route path="/charte-placement-bus" element={<ChartePlacementBus />} />
          <Route path="/charte-bus" element={<ChartePlacementBus />} />
          <Route path="/actualites" element={<News />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/conseils-pratiques" element={<PracticalAdvice />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/installer-l-appli" element={<InstallerAppli />} />
          <Route path="/installer" element={<InstallerAppli />} />
          <Route path="/tuto-inscription" element={<TutoInscription />} />
          <Route path="/simulateur-espagne" element={<SpainSimulator />} />
          <Route path="/voyages-passes" element={<CompletedTrips />} />
          {/* Iter 587 — adresse secrète du back-office ; /admin renvoie à l'accueil */}
          <Route path={ADMIN_PATH} element={<AdminDashboard />} />
          <Route path={ADMIN_PATH_ALIAS} element={<AdminDashboard />} />
          <Route path="/admin" element={<Navigate to="/" replace />} />
          <Route path="/apercu-verrouillage" element={<AccessGatePreview />} />
          <Route path="/politique-de-confidentialite" element={<PrivacyPolicy />} />
          <Route path="/mes-pieces/:token" element={<MesPieces />} />
          <Route path="/mon-bus/:token" element={<MonBus />} />
          <Route path="/espace-adherent" element={<EspaceAdherent />} />
          <Route path="/moi" element={<EspaceAdherent />} />
          <Route path="/verification-vol/:token" element={<VerificationVol />} />
          <Route path="/espace-adherent/:token" element={<EspaceAdherent />} />
          <Route path="/sondage/:sid/:token" element={<SondagePage />} />
          <Route path="/mentions-legales" element={<LegalNotices />} />
          <Route path="/cgv" element={<CGV />} />
          <Route path="/:slug" element={<GoSlug />} />
        </Routes>
      </Layout>
      </AccessGuard>
    </BrowserRouter>
  );
}

export default App;